import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { ClearCart, ClearOutStockCart } from '@features/cart'
import { useCartStore } from '@features/cart/model/useCartStore'
import { ProductCarouselNew } from '@features/products-carousel'

import CartBottomBar from '@widgets/cart-bottom-bar'
import { EmptyCart } from '@widgets/cart-empty'
import { CartEmptyInfo } from '@widgets/cart-empty-info'
import { CartTotal } from '@widgets/cart-total'
import { ProductCart, ProductCartCardProps } from '@widgets/product-cart'


import BackButton from '@shared/ui/back-button'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={3}
        width={'100%'}
        height={160}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function CartPage () {
  const { t } = useTranslation('common')
  const { t: tp } = useTranslation('product')
  const { t: tc } = useTranslation('cart')
  const {
    getTotalQuantity,
    getCart,
    getProduct,
    checkProductsInStock,
    cartInfoData,
    cartInfoLoading,
    isSyncing,
  } = useCartStore()
  const orderRef = useRef<HTMLDivElement>(null)
  const [ orderInView, setOrderInView ] = useState(false)
  const PRODUCT_COUNT = getTotalQuantity()
  const cart = getCart()

  const TOTAL_SUM = useMemo(() => {
    return cartInfoData?.totals?.total
  }, [ cartInfoData ])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([ entry ]) => setOrderInView(entry.isIntersecting),
      { threshold: 0.1 },
    )
    const currentRef = orderRef.current
    if (currentRef) observer.observe(currentRef)
    return () => {
      if (currentRef) observer.unobserve(currentRef)
    }
  }, [])

  useEffect(() => {
    checkProductsInStock()
  }, [])

  const isSmallMobile = useMediaQuery('(max-width: 375px)')

  let asideContent
  if (PRODUCT_COUNT) {
    asideContent = (
      <div ref={orderRef}>
        <Wrapper
          className={styles.orderWrapper}
          style={isSmallMobile ? { padding: '16px 8px' } : undefined}
        >
          <CartTotal />
        </Wrapper>

        {/* {!isMobile && (
          <Button
          >
            Поделиться корзиной
            <img
              src="/icons/share.svg"
              alt=""
              loading="lazy"
              fetchPriority="low"
            />
          </Button>
        )} */}
      </div>
    )
  }
  else {
    asideContent = (
      <div className={styles.asideStack}>
        <Wrapper>
          <CartEmptyInfo />
        </Wrapper>
      </div>
    )
  }

  const LOADING = useMemo(() => {
    return cartInfoLoading || isSyncing
  }, [ cartInfoLoading, isSyncing ])

  const IN_STOCK_PRODUCT = useMemo(() => {
    const products = cartInfoData?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const amount = cart.items.find((i) => i.product_id === p?.id)?.amount
      return amount && amount > 0
    })
  }, [ cartInfoData?.products, cart?.items ])

  const OUT_OF_STOCK_PRODUCTS = useMemo(() => {
    const products = cartInfoData?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const amount = cart.items.find((i) => i.product_id === p?.id)?.amount
      return amount === 0
    })
  }, [ cartInfoData?.products, cart?.items ])

  return (
    <div className={styles.root}>
      <Container>
        <PageGrid aside={asideContent}>
          <Wrapper>
            <div className={styles.root__grid}>
              <Breadcrumbs
                links={[
                  {
                    title: tc('title'),
                  },
                ]}
              />

              {!LOADING && (
                <div className={styles.root__top}>
                  <BackButton />

                  <TitleWithCount title={tc('title')}>
                    {PRODUCT_COUNT && (
                      <>
                        {getTotalQuantity()}{' '}
                        {getNoun(
                          getTotalQuantity(),
                          tp('product.1'),
                          tp('product.2'),
                          tp('product.3'),
                        )}
                      </>
                    )}
                  </TitleWithCount>
                </div>
              )}

              {LOADING && <SkeletonLoading />}

              {!PRODUCT_COUNT && <EmptyCart />}

              {PRODUCT_COUNT && !!OUT_OF_STOCK_PRODUCTS.length && !LOADING && (
                <div className={styles.root__group}>
                  <div className={styles['root__group-top']}>
                    {t('sold_out')}
                    <ClearOutStockCart />
                  </div>
                  <div className={styles.root__products}>
                    {OUT_OF_STOCK_PRODUCTS &&
                      OUT_OF_STOCK_PRODUCTS.map((p) => {
                        const product = getProduct(p?.id as string)
                        const amount = product?.amount as number

                        return (
                          <div
                            className={styles.root__item}
                            key={p?.id as string}
                          >
                            <ProductCart
                              data={p?.info as ProductCartCardProps['data']}
                              amount={amount}
                              outStockProduct
                            />
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              {IN_STOCK_PRODUCT &&
                !!IN_STOCK_PRODUCT.length &&
                PRODUCT_COUNT &&
                !LOADING && (
                <div className={styles.root__group}>
                  <div className={styles['root__group-top']}>
                    {tc('available')}
                    <ClearCart />
                  </div>
                  <div className={styles.root__products}>
                    {IN_STOCK_PRODUCT &&
                        IN_STOCK_PRODUCT.map((p) => {
                          const product = getProduct(p?.id as string)
                          const amount = product?.amount as number

                          return (
                            <div
                              className={styles.root__item}
                              key={p?.id as string}
                            >
                              <ProductCart
                                data={p?.info as ProductCartCardProps['data']}
                                amount={amount}
                              />
                            </div>
                          )
                        })}
                  </div>
                </div>
              )}

              {PRODUCT_COUNT && (
                <div className={styles.root__carousel}>
                  <ProductCarouselNew />
                </div>
              )}
            </div>
          </Wrapper>
        </PageGrid>
        <AnimatePresence>
          {PRODUCT_COUNT && !orderInView && TOTAL_SUM && (
            <CartBottomBar total={TOTAL_SUM} />
          )}
        </AnimatePresence>
      </Container>
    </div>
  )
}
